import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';

import supplierAPI from '../../api/supplierAPI';
import { logout } from '../../api/authAPI';

const SupplierList = () => {
    const [allSuppliers, setAllSuppliers] = useState([]); 
    const [filteredSuppliers, setFilteredSuppliers] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        paymentTerms: 'Net 30'
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchSuppliers();
      }, []);
      
     
    const fetchSuppliers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await supplierAPI.getAll();

           //console.log('fetch suppliers', response.data);
            
            // Add more defensive checks for API response
            if (response?.data) {

                setAllSuppliers(response.data);
                setFilteredSuppliers(response.data);
            } else {
                throw new Error('Invalid data format from API');
            }
        } catch (error) {
            setError(error.message || 'Failed to load suppliers');
            // Initialize empty arrays if API fails
            setAllSuppliers([]);
            setFilteredSuppliers([]);
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = async () => {
    try {
        await supplierAPI.delete(selectedSupplier._id);
        setAllSuppliers(allSuppliers.filter(s => s._id !== selectedSupplier._id));
        setConfirmModalOpen(false);
        fetchSuppliers();
    } catch (err) {
        setError(err.message || 'Failed to delete supplier');
    }
    };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedSupplier) {
        await supplierAPI.update(selectedSupplier._id, formData);
      } else {
        await supplierAPI.create(formData);
      }
      fetchSuppliers(); // Refresh the list after update
      setModalOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save supplier');
    }
  };

  const openAddModal = () => {
    setSelectedSupplier(null);
    setFormData({
      name: '',
      phone: '',
      paymentTerms: 'Net 30'
    });
    setModalOpen(true);
  };

  const openEditModal = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      phone: supplier.phone,
      paymentTerms: supplier.paymentTerms || 'Net 30'
    });
    setModalOpen(true);
  };

  // Handle search from DebouncedSearchInput
  const handleSearch = (term) => {
        setSearchTerm(term);
        
        // Ensure allSuppliers exists before filtering
        if (!allSuppliers || allSuppliers.length === 0 || !term) {
            setFilteredSuppliers(allSuppliers || []);
            return;
        }
        
        const lowercasedSearch = term.toLowerCase();
        const filtered = allSuppliers.filter(supplier => 
            (supplier.name?.toLowerCase() || '').includes(lowercasedSearch) ||
            (supplier.phone?.toLowerCase() || '').includes(lowercasedSearch) ||
            ((supplier.paymentTerms || 'Net 30').toLowerCase()).includes(lowercasedSearch)
        );
        setFilteredSuppliers(filtered);
    };


  const columns = [
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'name'
    },
    {
      title: 'Phone',
      key: 'phone',
      dataIndex: 'phone'
    },
    {
      title: 'Payment Terms',
      key: 'paymentTerms',
      render: (item) => item.paymentTerms || 'Net 30'
    }
  ];

  return (
    <Layout pageTitle="Supplier Management">
      <div className="bg-gray-100 mx-auto p-6">

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <DebouncedSearchInput
                placeholder="Search suppliers by name, phone, or terms..."
                onSearch={handleSearch}
                className="flex-1 mr-4 max-w-md"
            />

          <button 
            onClick={openAddModal} 
            className="transition-all duration-300 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center"
          >
            <span className="mr-1">+</span> Add Supplier
          </button>
        </div>

        <div className="mb-2">
          {searchTerm && (
            <div className="text-sm text-gray-500">
              Showing {filteredSuppliers.length} of {allSuppliers.length} suppliers matching "{searchTerm}"
            </div>
          )}
        </div>

        <DataTable
          columns={columns}
          data={filteredSuppliers || []}
          loading={loading}
          emptyMessage="No suppliers found"
          onEdit={openEditModal}
          onDelete={(supplier) => {
            setSelectedSupplier(supplier);
            setConfirmModalOpen(true);
          }}
        />

        {/* Supplier Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">
                  {selectedSupplier ? "Edit Supplier" : "Add Supplier"}
                </h2>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                      Supplier Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      placeholder="Supplier name"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone">
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      placeholder="Phone number"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="paymentTerms">
                      Payment Terms
                    </label>
                    <select
                      id="paymentTerms"
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="COD">COD</option>
                      <option value="Net 7">Net 7</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 60">Net 60</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => setModalOpen(false)}
                      className="mr-2 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-teal-500 hover:bg-teal-600 text-white py-2 px-4 rounded"
                    >
                      {selectedSupplier ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Delete Modal */}
        {confirmModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Confirm Delete</h2>
                <p className="mb-6">
                  Are you sure you want to delete the supplier "{selectedSupplier?.name}"? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setConfirmModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SupplierList;